/**
 * Minimal CSV utility: rfc4180-style escaping good enough for the
 * Expenses import/export use case (Excel-compatible).
 *
 * Export: `toCsv(rows, columns)` → string
 * Import: `parseCsv(text)` → { headers: string[], rows: object[] }
 */

const escapeCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes("\n") || s.includes('"')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

/**
 * Convert array of objects to CSV.
 * @param rows array of records
 * @param columns array of { key, label, format? } describing each column
 */
export function toCsv(rows, columns) {
  const header = columns.map((c) => escapeCell(c.label)).join(",");
  const body = rows.map((r) =>
    columns.map((c) => {
      const raw = r[c.key];
      const v = c.format ? c.format(raw, r) : raw;
      return escapeCell(v);
    }).join(",")
  ).join("\n");
  return `${header}\n${body}`;
}

export function downloadCsv(filename, csvText) {
  // BOM so Excel correctly detects UTF-8 (e.g. accented vendor names).
  const blob = new Blob(["\uFEFF" + csvText], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Parse CSV text into { headers, rows: object[] }.
 * Handles quoted cells, escaped quotes (""), and CRLF line endings.
 * Returns the rows as objects keyed by header name (lowercased for tolerance).
 */
export function parseCsv(text) {
  if (!text) return { headers: [], rows: [] };
  // Strip optional UTF-8 BOM Excel adds.
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  const cells = [];   // 2-D array of strings
  let row = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }
        else inQuotes = false;
      } else cell += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ",") { row.push(cell); cell = ""; }
      else if (ch === "\n") { row.push(cell); cells.push(row); row = []; cell = ""; }
      else if (ch === "\r") { /* ignore — CRLF handled by \n */ }
      else cell += ch;
    }
  }
  // Flush the final cell/row if the file didn't end with a newline.
  if (cell.length > 0 || row.length > 0) { row.push(cell); cells.push(row); }
  // Drop empty trailing rows that come from Excel-style files.
  const nonEmpty = cells.filter((r) => r.some((c) => c && c.trim() !== ""));
  if (nonEmpty.length === 0) return { headers: [], rows: [] };

  const headers = nonEmpty[0].map((h) => h.trim().toLowerCase());
  const rows = nonEmpty.slice(1).map((r) => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (r[i] ?? "").trim(); });
    return obj;
  });
  return { headers, rows };
}
